import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Dumbbell, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = '#EFC24E';

// Öffentliches Interessenten-Formular (iFrame-embeddbar), z.B. /interesse/rapperswil
// Design: Schwarz/Gelb passend zur StrongFit-Website
export default function PublicInterestPage() {
  const { source } = useParams();
  const leadSource = source || 'rapperswil';
  const [form, setForm] = useState({ first_name: '', email: '', phone: '', consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent) {
      setError('Bitte bestätige die Datenverwendung.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/public/interest-leads`, { ...form, source: leadSource });
      setDone(res.data.message || 'Danke für deine Anmeldung!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-lg bg-neutral-800 border border-neutral-700 text-white placeholder-neutral-500 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#EFC24E] focus:border-[#EFC24E]';

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-800 overflow-hidden" data-testid="interest-form-card">
        <div className="h-1.5 w-full" style={{ backgroundColor: GOLD }} />
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: GOLD }}>
              <Dumbbell className="h-6 w-6 text-black" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">StrongFit <span style={{ color: GOLD }}>Rapperswil</span></h1>
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: GOLD }}>Bald eröffnen wir!</p>
            </div>
          </div>

          {done ? (
            <div className="text-center py-10" data-testid="interest-success">
              <CheckCircle2 className="h-14 w-14 mx-auto mb-4" style={{ color: GOLD }} />
              <p className="text-lg font-semibold text-white">Du bist dabei! 🎉</p>
              <p className="text-sm text-neutral-400 mt-2">{done}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-neutral-300 mt-3 mb-5">
                Trag dich ein und erfahre als Erste/r alles über die Eröffnung,
                exklusive Eröffnungsangebote und Probetrainings in Rapperswil.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-200 mb-1">Vorname *</label>
                  <input
                    type="text" required value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="interest-name-input"
                    className={inputCls}
                    placeholder="Dein Vorname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-200 mb-1">E-Mail *</label>
                  <input
                    type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="interest-email-input"
                    className={inputCls}
                    placeholder="deine@email.ch"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-200 mb-1">
                    Mobile-Nummer <span className="text-neutral-500 font-normal">(für WhatsApp-Updates, optional)</span>
                  </label>
                  <input
                    type="tel" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="interest-phone-input"
                    className={inputCls}
                    placeholder="+41 79 123 45 67"
                  />
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer" data-testid="interest-consent-label">
                  <input
                    type="checkbox" checked={form.consent}
                    onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                    data-testid="interest-consent-checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-neutral-600 bg-neutral-800"
                    style={{ accentColor: GOLD }}
                  />
                  <span className="text-xs text-neutral-400 leading-relaxed">
                    Ich bin einverstanden, dass meine Daten <strong className="text-neutral-200">ausschliesslich für Informationen
                    zum neuen Studio Rapperswil</strong> verwendet werden (Eröffnung, Angebote, Probetrainings).
                    Keine Weitergabe an Dritte, Abmeldung jederzeit möglich. *
                  </span>
                </label>
                {error && <p className="text-sm text-red-400" data-testid="interest-error">{error}</p>}
                <button
                  type="submit" disabled={busy}
                  data-testid="interest-submit-btn"
                  className="w-full disabled:opacity-60 text-black font-bold uppercase tracking-wide rounded-lg py-3 text-sm transition-transform hover:scale-[1.02] flex items-center justify-center gap-2"
                  style={{ backgroundColor: GOLD }}
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Jetzt anmelden 💪
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
